/**
 * Clip Selector Component
 */

import React, { useEffect } from 'react';
import { useSessionsStore } from '../../stores/sessionsStore';
import { useSimulatorStore } from '../../stores/simulatorStore';
import { SelectedClip, Session } from '../../lib/types';
import { siteLabel } from '../../lib/siteTree';
import { SessionSelector } from '../SessionSelector';

interface ClipSelectorProps {
  defaultSessionId?: number | null;
  siteNames?: Map<number, string>;
  duplicateSessions?: Session[];
  checkingDuplicateClips?: boolean;
  onRefreshAccountSessions?: () => void;
}

const EMPTY_SITE_NAMES = new Map<number, string>();

export const ClipSelector: React.FC<ClipSelectorProps> = ({
  defaultSessionId,
  siteNames = EMPTY_SITE_NAMES,
  duplicateSessions = [],
  checkingDuplicateClips = false,
  onRefreshAccountSessions,
}) => {
  const { activeSessions, currentSite, loadSessions } = useSessionsStore();
  const { selectedClip, selectClip, isLoadingCriterion } = useSimulatorStore();

  useEffect(() => {
    if (currentSite && activeSessions.length === 0) {
      loadSessions();
    }
  }, [activeSessions.length, currentSite, loadSessions]);

  useEffect(() => {
    // Auto-select session if provided
    if (defaultSessionId && activeSessions.length > 0 && !selectedClip) {
      const session = activeSessions.find((s) => s.id === defaultSessionId);
      if (session) {
        handleSelectClip(session.id.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSessionId, activeSessions, selectedClip]);

  const handleSelectClip = (value: string) => {
    if (!value) {
      selectClip(null);
      return;
    }

    const sessionId = Number(value);
    const session = activeSessions.find((s) => s.id === sessionId);
    if (!session) return;

    const clip: SelectedClip = {
      id: session.id,
      clipAddress: session.clipAddress || '',
      clipName: session.clipName || '',
      client: session.client || '',
      diaperModelCode: session.diaperModelCode || null,
      away: session.away || false,
      monitored: session.monitored !== false,
      powerDownTime: session.powerDownTime || null,
      diaperId: session.diaperId || null,
      diaperCounter: session.diaperCounter || 0,
      firmwareRevision: session.firmwareRevision || 34,
      clipBatteryPercent: session.clipBatteryPercent || 80,
      wetnessLevel: session.wetnessLevel || 0,
      statusCode: session.statusCode || 0,
      adcValues: session.adcValues || null,
      rawSession: session,
    };

    selectClip(clip);
  };

  const handleSelectSession = (sessionId: number | null) => {
    handleSelectClip(sessionId?.toString() || '');
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <label className="block text-sm font-semibold mb-2">Select Session</label>
          <SessionSelector
            sessions={activeSessions}
            selectedSessionId={selectedClip?.id || null}
            onSelect={handleSelectSession}
            placeholder="Select a session..."
            className="w-full"
            siteNames={siteNames}
          />
        </div>
        <button
          onClick={() => {
            loadSessions();
            onRefreshAccountSessions?.();
          }}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          🔄 Refresh List
        </button>
      </div>

      {isLoadingCriterion && selectedClip && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-700">
          ⏳ Loading criterion configuration...
        </div>
      )}

      {checkingDuplicateClips && selectedClip && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
          Checking the account for reused clip addresses...
        </div>
      )}

      {selectedClip && duplicateSessions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          <div className="font-semibold">
            This clip address is active in {duplicateSessions.length} other session{duplicateSessions.length === 1 ? '' : 's'}.
          </div>
          <p className="mt-1">
            Nova processes simulator packets by clip address, so one packet can update every active session using
            {` ${selectedClip.clipAddress}`}.
          </p>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            {duplicateSessions.slice(0, 5).map((session) => (
              <li key={session.id}>
                {session.client || `Session ${session.id}`} - {session.clipName || session.clipAddress || 'Unknown clip'} - {siteLabel(session.siteId, siteNames)}
              </li>
            ))}
          </ul>
          {duplicateSessions.length > 5 && (
            <div className="mt-1 text-xs">+{duplicateSessions.length - 5} more duplicate session{duplicateSessions.length - 5 === 1 ? '' : 's'}</div>
          )}
        </div>
      )}
    </div>
  );
};
